import { Group, Stack, Text } from "@mantine/core";
import { LoaderFunction, MetaFunction } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import {
  IconBriefcase,
  IconCalendar,
  IconClock,
  IconShare,
} from "@tabler/icons-react";
import { useState } from "react";

import Image from "~/assets/misc/masterclass.webp";
import PlaceholderImage from "~/assets/misc/placeholder-person.png";
import { PAGE_TITLE } from "~/constants";
import { useTranslation } from "~/hooks/use-typed-translation";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import { getMasterclassesByDoctor } from "~/services/get-masterclasses-by-doctor";
import utilityClasses from "~/styles/utilities.module.scss";
import { Masterclass } from "~/types/masterclass";
import { resolveCurrencyContext } from "~/utils/currency.server";
import { formatCurrency } from "~/utils/format-currency";
import { getCentralizedMeta } from "~/utils/meta-helper";

import classes from "./masterclass.module.scss";

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];
  
  const masterclassesData = data.data as Masterclass[];
  const doctorImage = masterclassesData?.at(0)?.providerDetails?.image;

  return getCentralizedMeta({
    title: data.metaTitle,
    description: data.metaDescription,
    image: doctorImage,
    url: data.metaOrigin + location.pathname,
    locale: data.metaLocale,
    keywords: `Ivory Coast, Côte d'Ivoire, Masterclass`,
    alternateLanguages: {
      en: `${data.metaOrigin}/en/doctor/${data.slug}/masterclass`,
      fr: `${data.metaOrigin}/fr/doctor/${data.slug}/masterclass`,
    }
  });
};

export default function Route() {
  const { t, i18n } = useTranslation();

  // const [copied, setCopied] = useState(false);
  // const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const loaderData = useLoaderData<typeof loader>();
  const masterclassesData = loaderData?.data as Masterclass[];
  const currency = loaderData?.currency;

  const handleShare = async (masterclass: Masterclass) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: masterclass.name,
          text: t("share-masterclass-text"),
          url: `${window.location.origin}/${i18n.language}/masterclass/${masterclass.slug}`,
        });
        // console.log("Share successful!");
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      alert(t("sharing-not-supported"));
    }
  };

  return (
    <section className={classes.section}>
      <div className={utilityClasses.container}>
        <div className={classes["header-wrapper"]}>
          <h1 className={classes.heading}>{t("join-my-masterclasses")}!</h1>
          <Group gap={10} mt={"1rem"} align="center">
            <div className={classes["img-wrapper"]}>
              <img
                src={
                  masterclassesData?.at(0)?.providerDetails?.image ??
                  PlaceholderImage
                }
                alt=""
                width={320}
                height={300}
              />
            </div>

            <Stack gap={0}>
              <Text fz={16} fw={600}>
                {masterclassesData?.at(0)?.providerDetails?.fullName}
              </Text>
              <Group gap={5}>
                <IconBriefcase size={16} stroke={0.5} />
                <Text span fz={13} c="dimmed">
                  {masterclassesData?.at(0)?.providerDetails?.specialization}
                </Text>
              </Group>
            </Stack>
          </Group>
        </div>

        <div className={classes["cards-wrapper"]}>
          {masterclassesData?.map((masterclass: Masterclass) => (
            <div className={classes.card} key={masterclass.id}>
              <div className={classes["img-wrapper"]}>
                <img
                  src={masterclass?.banner ?? Image}
                  alt={masterclass?.name}
                />
              </div>

              <div className={classes["text-content"]}>
                <h2>{masterclass.name}</h2>
                <Group gap={10} align="center" mt="1.5rem">
                  <Group gap={5} align="center">
                    <IconCalendar stroke={1.5} size={20} />
                    <Text span fz={15} fw={500}>
                      {t("date")}:
                    </Text>
                  </Group>
                  <Text fz={15}>
                    {new Date(masterclass.dateTime).toLocaleDateString(i18n.language, {
                      dateStyle: "long",
                    })}
                  </Text>
                </Group>

                <Group gap={10} align="center" mt="0.5rem">
                  <Group gap={5} align="center">
                    <IconClock stroke={1.5} size={20} />
                    <Text span fz={15} fw={500}>
                      {t("time")}:
                    </Text>
                  </Group>
                  <Text fz={15}>
                    {new Date(masterclass.dateTime).toLocaleTimeString(i18n.language, {
                      timeStyle: "short",
                    })}
                  </Text>
                </Group>

                <Group justify="space-between" align="end" mt="2rem">
                  <Stack gap={5}>
                    <Text fz={14}>{t("price")}:</Text>
                    <Text fw={600} fz={18}>
                      {formatCurrency(
                        masterclass.price,
                        i18n.language,
                        currency
                      )}
                    </Text>
                  </Stack>

                  <div className={classes["btns-wrapper"]}>
                    <Link
                      to={`/${i18n.language}/masterclass/${masterclass.slug}`}
                      reloadDocument>
                      {t("enroll")}
                    </Link>
                    {/* <button
                      onClick={() =>
                        copyToClipboard(value => {
                          setCopied(value);
                          setCopiedSlug(value ? masterclass.slug : null);
                        }, masterclass.slug)
                      }>
                      {copied && copiedSlug === masterclass.slug ? (
                        <IconCheck size={16} stroke={1.5} />
                      ) : (
                        <IconShare size={16} stroke={1.5} />
                      )}
                    </button> */}
                    <button onClick={() => handleShare(masterclass)}>
                      <IconShare size={16} stroke={1.5} />
                    </button>
                  </div>
                </Group>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;
  const slug = params.slug ?? "";

  const currencyContext = (await resolveCurrencyContext(request, true)) ?? {
    currency: "XOF",
    fxRate: 1,
    country: "ci",
  };
  const { currency, fxRate } = currencyContext;

  const titleText =
    locale === "fr" ? `Rejoignez mes masterclasses` : `Join masterclasses`;

  const title = `${titleText} • ${PAGE_TITLE}`;

  const masterClassesRes = await getMasterclassesByDoctor(slug);
  if (masterClassesRes.ok) {
    masterClassesRes.data = masterClassesRes.data.map((masterclass: Masterclass) => ({
      ...masterclass,
      price: masterclass.price * fxRate,
    }));
  }
  
  const doctorName = masterClassesRes.data?.at(0)?.providerDetails?.fullName || "";
  const descText = locale === "fr"
    ? `Découvrez les masterclasses proposées par ${doctorName} sur La Ruche Santé.`
    : `Explore masterclasses by ${doctorName} on La Ruche Health.`;

  // console.log(JSON.stringify(masterClassesRes));
  return {
    metaTitle: title,
    metaDescription: descText,
    metaOrigin: origin,
    metaLocale: locale,
    slug,
    currency,
    data: masterClassesRes.data,
  };
};
